import type { ReactNode } from 'react';
import { PageShell, GetStarted } from '../../components';
import { PageBody, PageHero, Section, FeatureRows, CardGrid, Prose } from '../../components/page';
import type { Row } from '../../components/page';
import type { PaintingName } from '../../components/Painting';

/**
 * Shared layout for the three persona pages (/use-cases/developers,
 * /providers, /enterprise), built on the interior page template.
 *
 * Order: the problem the reader already has, what Statio does about it, an
 * optional product window showing the real screen, any persona-specific
 * sections, then the other personas and the closing band.
 *
 * Every claim on these pages must hold against .impeccable/product-truth.md.
 */

export interface UseCaseScreen {
  title: string;
  lede: ReactNode;
  /** A window from src/components/product. */
  window: ReactNode;
}

export interface UseCaseData {
  title: string;
  subtitle: string;
  /** The page's one painting, shown as the hero banner. */
  art: PaintingName;
  problemTitle: string;
  problemText: string;
  solutionTitle: string;
  solutionText: string;
  benefits: Row[];
  screen?: UseCaseScreen;
  /** Persona-specific sections, rendered after the screen. */
  extra?: ReactNode;
  otherCases: { label: string; slug: string; body: string }[];
}

export default function UseCaseLayout({ data }: { data: UseCaseData }) {
  return (
    <PageShell>
      <PageBody>
        <PageHero title={data.title} lede={data.subtitle} art={data.art} />

        <Section id="problem" title={data.problemTitle} split>
          <Prose>
            <p>{data.problemText}</p>
          </Prose>
        </Section>

        <Section id="solution" title={data.solutionTitle} lede={data.solutionText}>
          <FeatureRows items={data.benefits} />
        </Section>

        {data.screen && (
          <Section id="in-the-app" title={data.screen.title} lede={data.screen.lede} split>
            {data.screen.window}
          </Section>
        )}

        {data.extra}

        <Section id="other-use-cases" title="Other use cases">
          <CardGrid
            items={data.otherCases.map((c) => ({
              title: c.label,
              body: c.body,
              href: `/use-cases/${c.slug}`,
              linkLabel: 'Read more',
            }))}
          />
        </Section>
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
